import { z } from 'zod';
import { nonEmptyPatch } from './_patch.js';

const trimmedName = z
  .string()
  .min(1)
  .max(200)
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, 'must not be blank');

const trimmedContacts = z
  .string()
  .min(1)
  .max(2000)
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, 'must not be blank');

/**
 * @aiPurpose canonical wire shape for a RU-internal carrier row. The
 *   directory is distinct from the global `Carrier` table (which is
 *   semantically the China→RU carrier list consumed by `/china-rates`);
 *   `RussiaCarrier` is the ad-hoc carriers list `logist_ru` maintains
 *   per-request for Russia-side logistics. Contacts is a single
 *   free-form text field — no structured sub-fields by design (#1166
 *   decision §4).
 * @aiInvariant `name` is trimmed and rejected when blank; length ≤ 200.
 *   `contacts` is required and length ≤ 2000. `active = false` is the
 *   archived state; no hard delete exists. The DB enforces no name
 *   uniqueness in v1 (the directory is small and operator-curated).
 * @aiSecurity the `contacts` field holds carrier-side personal data
 *   (phones, e-mails, messengers) that `manager` and `logist_cn` MUST
 *   NOT see; this is gated at the controller with `russiaCarriers.read`
 *   (held only by `logist_ru` + `admin` via the ALL sentinel).
 * @aiPii `contacts`
 */
export const RussiaCarrierSchema = z
  .object({
    id: z.string().uuid(),
    name: trimmedName,
    contacts: trimmedContacts,
    active: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type RussiaCarrier = z.infer<typeof RussiaCarrierSchema>;

/**
 * @aiPurpose `POST /api/russia-carriers` body. `active` is not
 *   accepted — the server defaults newly-created rows to `true` and the
 *   archive lifecycle owns the flag via `DELETE`.
 */
export const CreateRussiaCarrierBodySchema = z
  .object({
    name: trimmedName,
    contacts: trimmedContacts,
  })
  .strict();
export type CreateRussiaCarrierBody = z.infer<typeof CreateRussiaCarrierBodySchema>;

/**
 * @aiPurpose `PATCH /api/russia-carriers/:id` body. `active` is exposed
 *   on the patch (mirrors the dictionary-page "unarchive" affordance);
 *   the dedicated `DELETE` endpoint remains the soft-archive happy path.
 */
export const UpdateRussiaCarrierBodySchema = nonEmptyPatch(
  z
    .object({
      name: trimmedName,
      contacts: trimmedContacts,
      active: z.boolean(),
    })
    .partial()
    .strict(),
);
export type UpdateRussiaCarrierBody = z.infer<typeof UpdateRussiaCarrierBodySchema>;

const stringBoolean = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => (typeof v === 'boolean' ? v : v === 'true'));

/**
 * @aiPurpose query string for `GET /api/russia-carriers`. `search`
 *   matches normalized `name` with case-insensitive substring
 *   semantics. `active` is unset by default (returns both active and
 *   archived rows) so the dictionary page can render both filters from
 *   a single fetch.
 * @aiInvariant `.strict()` rejects unknown keys.
 */
export const ListRussiaCarriersQuerySchema = z
  .object({
    search: z.string().min(1).max(200).optional(),
    active: stringBoolean.optional(),
  })
  .strict();
export type ListRussiaCarriersQuery = z.infer<typeof ListRussiaCarriersQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/russia-carriers`. No
 *   cursor — the directory is intentionally small (operator-curated
 *   per-request additions; not auto-populated).
 */
export const ListRussiaCarriersResponseSchema = z
  .object({
    items: z.array(RussiaCarrierSchema),
  })
  .strict();
export type ListRussiaCarriersResponse = z.infer<typeof ListRussiaCarriersResponseSchema>;
